/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import useAxiosPrivate from "layouts/authentication/hooks/useAxiosPrivate";

// @mui material components
import Container from "@mui/material/Container";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import { map as pluck } from "lodash";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Pricing page components
import DefaultPricingCard from "examples/Cards/PricingCards/DefaultPricingCard";
import Faq from "layouts/pages/pricing-page/components/Faq";
import useAuth from "layouts/authentication/hooks/useAuth";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import { Error } from "@mui/icons-material";
import { fetchSpecialPlan, fetchPlans } from "services/plan";

function PricingPage() {
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const location = useLocation();

  const { secret } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const currentPlanID = auth?.plan?.id ?? auth?.access_token ? 1 : 0;
  const currentPlanLevel = auth && auth.plan ? auth.plan.level : auth && auth.access_token ? 1 : 0;

  useEffect(() => {
    fetchPlans().then((data) => {
      if (data && data.length > 0) {
        setPlans(data);

        fetchSpecialPlan(secret)
          .then((data) => {
            if (data) {
              setPlan({
                ...data.special_plan?.plan,
                free_months: data.special_plan?.free_months,
                cb_plan_id: data.special_plan?.cb_plan_id,
              });
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    });
  }, []);

  const getLowerPlanSpecifications = (currentPlan) => {
    if (currentPlan.level <= 1) return [];

    const lowerPlans = plans.filter((plan) => plan.level < currentPlan.level);
    const planNames = pluck(lowerPlans, "name");

    return planNames;
  };

  const openCBCheckout = (plan_id) => {
    if (!window.Chargebee) {
      alert("Chargebee script not loaded yet!");
      return;
    }

    const chargebee = window.Chargebee.init({
      site: process.env.REACT_APP_CB_SITE,
    });

    chargebee.openCheckout({
      layout: "in_app",
      hostedPage: async () => {
        // Call backend API which creates hosted page
        const res = await axiosPrivate.post(
          "/chargebee/checkout/hosted_page",
          {
            plan_id,
            paying_mode: "annual",
            secret,
          },
          {
            withCredentials: true,
          },
        );

        // returns hosted_page object from Chargebee
        return res.data.hosted_page;
      },

      success: async (hostedPageId) => {
        console.log("Checkout success:");
      },

      close: () => {
        console.log("Checkout closed");
      },

      step: (value) => {
        console.log("Checkout step:", value);
      },
    });
  };

  const join = async (planID, planLevel) => {
    //check if user already logged in
    //redirect to login disabled for now
    if (auth?.access_token) {
      if (planLevel < auth.plan?.level) {
        if (!window.confirm("Are you sure you want to downgrade your subscription?")) return;
      }

      openCBCheckout(planID, 1);

      return;
    }

    navigate("/dashboard/authentication/sign-in", {
      // state: { from: { ...location, pathname: location.pathname + "/join/" + planID } },
      state: { from: location },
      replace: true,
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar pageTitle="Special Plan just for You" />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Container>
            {isLoading ? (
              <SoftBox px={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress color="inherit" />
              </SoftBox>
            ) : (
              <SoftBox style={{ maxWidth: "350px", margin: "0 auto" }}>
                {plan ? (
                  <DefaultPricingCard
                    badge={{ color: "secondary", label: plan.name }}
                    price={{
                      currency: "USD ",
                      value: plan.price_annually,
                    }}
                    mode={1}
                    specifications={plan.plan_features.available}
                    specificationsFromLowerPlans={getLowerPlanSpecifications(plan)}
                    action={{
                      type: "callback",
                      callback: () => join(plan.id, plan.level),
                      label: currentPlanID === 0 ? "join now" : currentPlanLevel > plan.level ? "Downgrade" : "Upgrade",
                    }}
                    specialMessage={`With ${plan.free_months} months free trial!`}
                  />
                ) : (
                  <Alert icon={<Error fontSize="inherit" />} severity="error">
                    Sorry! This link is invalid, or the offer has already been availed.
                  </Alert>
                )}
              </SoftBox>
            )}
          </Container>
          <Container>
            <Faq />
          </Container>
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PricingPage;
